"use client";

import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function TribeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent/20 border border-border rounded-2xl text-center">
        <p className="text-foreground font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-lg">
          Register another email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 w-full">
        <FormField control={control} name="name" rules={{
        required: "Please enter your name",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1">
              <FormLabel className="sr-only">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Your name" className="h-12 rounded-lg px-5 bg-background border-border focus-visible:border-primary focus-visible:ring-primary/20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem className="flex-[1.5]">
              <FormLabel className="sr-only">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" className="h-12 rounded-lg px-5 bg-background border-border focus-visible:border-primary focus-visible:ring-primary/20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <Button type="submit" disabled={isSubmitting} className="h-12 px-7 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-default font-semibold transition-all duration-200 hover:scale-[1.03] hover:-translate-y-0.5 active:scale-[0.98]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Joining...
            </> : "Join"}
        </Button>
      </div>

      <div className="space-y-3 pt-2">
        <FormField control={control} name="interests" render={({
        field
      }) => {
        const interestOptions = ["New products", "Hygiene tips", "Exclusive updates"];
        const currentValues: string[] = Array.isArray(field.value) ? field.value : [];
        const toggleInterest = (option: string) => {
          if (currentValues.includes(option)) {
            field.onChange(currentValues.filter(v => v !== option));
          } else {
            field.onChange([...currentValues, option]);
          }
        };
        return <FormItem>
                <FormLabel className="text-sm text-muted-foreground font-default">
                  I'm interested in
                </FormLabel>
                <div className="flex flex-wrap gap-2 justify-center pt-1">
                  {interestOptions.map((option, i) => {
              const isSelected = currentValues.includes(option);
              return <Button key={option} data-index={i} type="button" onClick={() => toggleInterest(option)} variant={isSelected ? "default" : "outline"} className="rounded-lg font-default font-medium">
                        {option}
                      </Button>;
            })}
                </div>
                <FormMessage />
              </FormItem>;
      }} />

        <FormField control={control} name="consent" rules={{
        required: "Please confirm to continue"
      }} render={({
        field
      }) => <FormItem>
              <label className="flex items-start gap-3 cursor-pointer justify-center">
                <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} className="mt-1 size-4 rounded border-border accent-primary cursor-pointer" />
                <span className="text-sm text-muted-foreground font-default text-left">
                  I agree to receive updates from B.Y.E. Hygiene Mist™. You can unsubscribe at any time.
                </span>
              </label>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}
    </Form>;
}
export default function JoinThePreparedCommunity() {
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const subtext = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const formWrap = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const caption = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const baseReveal = "transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]";
  const hidden = "opacity-0 translate-y-4";
  const shown = "opacity-100 translate-y-0";
  return <section data-section-id="16307" id="join-the-prepared-community" className="dark bg-background text-foreground py-20 md:py-24 px-6 md:px-10">
      <div className="max-w-[680px] mx-auto text-center">
        <h2 ref={heading.ref} className={`font-default font-semibold text-foreground tracking-tight leading-[1.08] text-[clamp(2rem,3.6vw,3.25rem)] ${baseReveal} ${heading.isVisible ? shown : hidden}`}>
          Join the{" "}
          <span className="font-serif italic font-medium text-primary">prepared</span>{" "}
          community
        </h2>

        <p ref={subtext.ref} className={`mt-5 text-muted-foreground font-default text-[clamp(1.05rem,1.15vw,1.2rem)] leading-[1.55] ${baseReveal} ${subtext.isVisible ? shown : hidden}`} style={{
        transitionDelay: "80ms"
      }}>
          Hygiene habits, travel preparedness guides, and early access to our latest mist collections.
        </p>

        <div ref={formWrap.ref} className={`mt-10 ${baseReveal} ${formWrap.isVisible ? shown : hidden}`} style={{
        transitionDelay: "160ms"
      }}>
          <FormProvider formKey="join_community_form_preparedness_hub" formId="join_community_form_preparedness_hub" sectionName="join-the-prepared-community" defaultValues={{
          name: "",
          email: "",
          interests: [],
          consent: false
        }} validationRules={{
          name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          consent: {
            required: true
          }
        }} submitText="Join" successMessage="You're in! Welcome to the B.Y.E. family." errorMessage="Something went wrong. Please try again.">
            <TribeForm />
          </FormProvider>
        </div>

        <p ref={caption.ref} className={`mt-6 text-sm text-muted-foreground font-default ${baseReveal} ${caption.isVisible ? shown : hidden}`} style={{
        transitionDelay: "240ms"
      }}>
          No spam — just helpful hygiene insights.
        </p>
      </div>
    </section>;
}